import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, WHATSAPP_URL } from "@/lib/constants";

const links = [
    { id: "servicos", label: "Serviços" },
    { id: "porque-escolher", label: "Diferenciadores" },
    { id: "galeria", label: "Galeria" },
    { id: "sobre", label: "Sobre" },
    { id: "contacto", label: "Contacto" },
];

const useScrolled = () => {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return scrolled;
};

const DesktopNav = ({ onNavigate }) => (
    <nav className="hidden lg:flex items-center gap-10">
        {links.map((l) => (
            <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => onNavigate(l.id)}
                className="text-xs uppercase tracking-[0.18em] text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors"
            >
                {l.label}
            </button>
        ))}
    </nav>
);

const DesktopActions = () => (
    <div className="hidden lg:flex items-center gap-3">
        <a
            data-testid="navbar-phone"
            href={`tel:${COMPANY.phoneIntl}`}
            className="flex items-center gap-2 text-sm text-[#0A0A0A]/80 hover:text-[#0A0A0A]"
        >
            <Phone className="w-4 h-4 text-[#9B2C2C]" />
            {COMPANY.phone}
        </a>
        <a
            data-testid="navbar-quote-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="bg-[#9B2C2C] text-white hover:bg-[#B83535] transition-colors font-bold tracking-wider uppercase text-xs px-6 py-3"
        >
            Pedir Orçamento
        </a>
    </div>
);

const MobileMenu = ({ onNavigate }) => (
    <div
        data-testid="mobile-menu"
        className="lg:hidden bg-white border-t border-black/10 px-6 py-6 space-y-4"
    >
        {links.map((l) => (
            <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => onNavigate(l.id)}
                className="block w-full text-left text-sm uppercase tracking-[0.18em] text-[#0A0A0A]/80 py-2"
            >
                {l.label}
            </button>
        ))}
        <a
            data-testid="mobile-quote-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="block text-center bg-[#9B2C2C] text-white font-bold tracking-wider uppercase text-xs px-6 py-4"
        >
            Pedir Orçamento
        </a>
    </div>
);

const Navbar = () => {
    const scrolled = useScrolled();
    const [open, setOpen] = useState(false);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-xl border-b border-black/10 shadow-sm"
                    : "bg-white/85 backdrop-blur-md"
            }`}
        >
            <div className="px-6 md:px-12 lg:px-24">
                <div className="flex items-center justify-between h-24 md:h-28">
                    <button
                        data-testid="navbar-logo"
                        onClick={() => scrollTo("hero")}
                        className="flex items-center gap-3 group"
                    >
                        <img
                            src="/logo.jpeg"
                            alt="Lavlimpar"
                            className="h-12 md:h-14 w-auto object-contain"
                        />
                    </button>

                    <DesktopNav onNavigate={scrollTo} />
                    <DesktopActions />

                    <button
                        data-testid="navbar-mobile-toggle"
                        className="lg:hidden p-2 text-[#0A0A0A]"
                        onClick={() => setOpen((s) => !s)}
                        aria-label="Menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {open && <MobileMenu onNavigate={scrollTo} />}
        </header>
    );
};

export default Navbar;
